import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { computeScore } from "./score";
import { STATUS_COLORS } from "./rooms";

const STATUS_LABEL = { ideal: "Ideal", ok: "Acceptable", conflict: "Conflict" };
export const hexToRgb = (hex) => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };

export const AMBER = [180, 83, 9];
export const INK = [15, 23, 42];
export const MUTED = [100, 116, 139];
export const MARGIN = 14;
export const TABLE_STYLES = {
  styles: { font: "helvetica", fontSize: 8, cellPadding: 2, textColor: INK, lineColor: [226, 232, 240], lineWidth: 0.2 },
  headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: "bold" },
  alternateRowStyles: { fillColor: [248, 250, 252] },
};

export const pageSize = (doc) => ({ w: doc.internal.pageSize.getWidth(), h: doc.internal.pageSize.getHeight() });
export const hasBranding = (b) => !!(b && (b.consultant_name || b.firm || b.logo_data_url || b.phone || b.email || b.website));

// Build a print-ready PDF: branded header + all three maps (8/16/32 divisions) + optional room report.
// maps: [{ sectorCount, canvas }] — one rendered snapshot per division.
export function buildVastuPDF({ maps, planName, rotation, center, area, pageFormat = "a4", branding = null, markers = [], includeRoomReport = true, withFooters = true }) {
  const first = maps[0].canvas;
  const landscape = first.width > first.height * 1.15;
  const orient = landscape ? "landscape" : "portrait";
  const doc = new jsPDF({ orientation: orient, unit: "mm", format: pageFormat });
  const title = planName || "Untitled plan";

  drawCoverSheet(doc, { branding, title, rotation, center, area, markers });

  maps.forEach((m) => {
    doc.addPage(pageFormat, orient);
    drawPageTitle(doc, `${m.sectorCount} divisions`, title, branding);
    drawSnapshot(doc, m.canvas);
    drawElementLegend(doc);
  });

  if (includeRoomReport && markers.length > 0) {
    doc.addPage(pageFormat, "portrait");
    drawPageTitle(doc, "Room Placement Report", title, branding);
    const report = computeScore(markers, center, rotation);
    drawScoreBlock(doc, report);
    drawRoomTable(doc, markers, report);
  }

  if (withFooters) drawFooters(doc, branding);
  return doc;
}

// Dedicated title page: plan name, date and consultant branding, shown before the maps.
function drawCoverSheet(doc, { branding, title, rotation, center, area, markers }) {
  const { w, h } = pageSize(doc);
  const branded = hasBranding(branding);
  const cx = w / 2;

  // Deep background band
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, h, "F");
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, w, 4, "F");
  doc.rect(0, h - 4, w, 4, "F");

  // Logo (centered) or compass mark
  let y = h * 0.24;
  if (branding?.logo_data_url) {
    try {
      const props = doc.getImageProperties(branding.logo_data_url);
      const logoH = 26;
      const logoW = Math.min(70, (props.width / props.height) * logoH);
      doc.addImage(branding.logo_data_url, props.fileType || "PNG", cx - logoW / 2, y - logoH, logoW, logoH);
    } catch (e) { /* ignore bad logo */ }
  }

  // Eyebrow
  y = h * 0.36;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...AMBER);
  doc.text("VASTU SECTOR ANALYSIS", cx, y, { align: "center", charSpace: 1.5 });

  // Plan title
  y += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(30);
  doc.setTextColor(245, 245, 245);
  const titleLines = doc.splitTextToSize(title, w - MARGIN * 4);
  doc.text(titleLines, cx, y, { align: "center" });
  y += titleLines.length * 12;

  // Firm / consultant name under title
  if (branded && (branding.firm || branding.consultant_name)) {
    y += 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(203, 213, 225);
    doc.text(branding.firm || branding.consultant_name, cx, y, { align: "center" });
    y += 8;
  }

  // Divider
  y += 4;
  doc.setDrawColor(...AMBER);
  doc.setLineWidth(0.6);
  doc.line(cx - 18, y, cx + 18, y);
  doc.setLineWidth(0.2);

  // Meta chips
  y += 12;
  const meta = [
    ["DATE", new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })],
    ["TRUE NORTH", `${rotation.toFixed(1)}°`],
    ["MAP DIVISIONS", "8 · 16 · 32"],
    ["ROOM MARKERS", String(markers.length)],
  ];
  if (area > 0) meta.push(["PLOT AREA (PX²)", area.toFixed(0)]);
  doc.setFontSize(8);
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    doc.text(k, cx - 30, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(226, 232, 240);
    doc.text(String(v), cx + 30, y, { align: "right" });
    doc.setDrawColor(51, 65, 85);
    doc.line(cx - 30, y + 2.5, cx + 30, y + 2.5);
    y += 8;
  });

  // Consultant contact block (bottom)
  if (branded) {
    const contact = [
      branding.consultant_name && branding.firm ? branding.consultant_name : null,
      [branding.phone, branding.email].filter(Boolean).join("   ·   "),
      branding.website,
    ].filter(Boolean);
    let cyy = h - 22;
    contact.forEach((line, i) => {
      doc.setFont("helvetica", i === 0 ? "bold" : "normal");
      doc.setFontSize(i === 0 ? 10 : 8);
      doc.setTextColor(...(i === 0 ? [226, 232, 240] : [148, 163, 184]));
      doc.text(String(line), cx, cyy, { align: "center" });
      cyy += i === 0 ? 5 : 4;
    });
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("Vastu Mandala · Floor Plan Sector Analyzer", cx, h - 12, { align: "center" });
  }
}

function drawAccentBar(doc) {
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, pageSize(doc).w, 3, "F");
}

const INCH = 25.4; // 1 inch in mm

// Compact 5-element colour key placed in the bottom margin of each map page.
const ELEMENT_LEGEND = [
  ["Water (N · NE)", "#2563EB"],
  ["Air / Wood (E)", "#16A34A"],
  ["Fire (SE · S)", "#DC2626"],
  ["Earth (SW)", "#CA8A04"],
  ["Space (W · NW)", "#94A3B8"],
];

function drawElementLegend(doc) {
  const { w, h } = pageSize(doc);
  const y = h - 14;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  const gap = 6, dot = 2.2;
  const widths = ELEMENT_LEGEND.map(([label]) => dot + 2 + doc.getTextWidth(label));
  const total = widths.reduce((a, b) => a + b, 0) + gap * (ELEMENT_LEGEND.length - 1);
  let x = (w - total) / 2;
  ELEMENT_LEGEND.forEach(([label, color], i) => {
    doc.setFillColor(...hexToRgb(color));
    doc.circle(x + dot / 2, y - 1, dot / 2, "F");
    doc.setTextColor(...INK);
    doc.text(label, x + dot + 2, y);
    x += widths[i] + gap;
  });
}

function drawSnapshot(doc, canvas) {
  const { w, h } = pageSize(doc);
  const boxX = INCH, boxY = INCH;
  const boxW = w - INCH * 2, boxH = h - INCH * 2;
  const ratio = Math.min(boxW / canvas.width, boxH / canvas.height);
  const drawW = canvas.width * ratio;
  const drawH = canvas.height * ratio;
  const x = boxX + (boxW - drawW) / 2;
  const y = boxY + (boxH - drawH) / 2;
  doc.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", x, y, drawW, drawH);
  doc.setDrawColor(203, 213, 225);
  doc.rect(x, y, drawW, drawH);
}

export function drawPageTitle(doc, heading, subtitle, branding) {
  drawAccentBar(doc);
  const { w } = pageSize(doc);
  let textX = MARGIN;
  if (branding?.logo_data_url) {
    try {
      const props = doc.getImageProperties(branding.logo_data_url);
      const logoH = 12;
      const logoW = Math.min(34, (props.width / props.height) * logoH);
      doc.addImage(branding.logo_data_url, props.fileType || "PNG", MARGIN, 6, logoW, logoH);
      textX = MARGIN + logoW + 5;
    } catch (e) { /* ignore bad logo */ }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...INK);
  doc.text(heading, textX, 13);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(subtitle, textX, 18.5);
  if (hasBranding(branding)) {
    const name = branding.firm || branding.consultant_name || "";
    const contact = [branding.phone, branding.email].filter(Boolean).join("   ·   ");
    if (name) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(...INK);
      doc.text(name, w - MARGIN, 13, { align: "right" });
    }
    if (contact) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...MUTED);
      doc.text(contact, w - MARGIN, 18, { align: "right" });
    }
  }
}

const SCORE_BOX = { y: 25, h: 26 };

function drawScoreBlock(doc, report) {
  const { w } = pageSize(doc);
  const { y, h } = SCORE_BOX;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(MARGIN, y, w - MARGIN * 2, h, 2, 2, "FD");

  if (report.score === null) {
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text("Draw the house polygon to compute directions and a compliance score.", MARGIN + 6, y + 15);
    return;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...report.grade.color);
  doc.text(String(report.score), MARGIN + 6, y + 17);
  const scoreW = doc.getTextWidth(String(report.score));
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("/ 100", MARGIN + 6 + scoreW + 2, y + 17);
  doc.setFontSize(11);
  doc.setTextColor(...report.grade.color);
  doc.text(report.grade.label, MARGIN + 34, y + 11);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("Vastu compliance score · weighted by room importance", MARGIN + 34, y + 16);

  let lx = w - MARGIN - 6;
  [["Conflict", "conflict"], ["Acceptable", "ok"], ["Ideal", "ideal"]].forEach(([lbl, key]) => {
    const txt = `${report.counts[key]} ${lbl}`;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...hexToRgb(STATUS_COLORS[key]));
    lx -= doc.getTextWidth(txt);
    doc.text(txt, lx, y + 14);
    lx -= 8;
  });
  doc.setFont("helvetica", "normal");
}

function drawRoomTable(doc, markers, report) {
  const evalFor = (id) => report.evals.find((x) => x.marker.id === id);
  autoTable(doc, {
    ...TABLE_STYLES,
    startY: SCORE_BOX.y + SCORE_BOX.h + 6,
    margin: { left: MARGIN, right: MARGIN },
    head: [["#", "Room", "Zone", "Sanskrit", "Status", "Ideal"]],
    body: markers.map((m, i) => {
      const e = evalFor(m.id);
      if (!e) return [i + 1, m.type, "—", "—", "—", "—"];
      return [i + 1, e.room.label, e.direction, e.info.sanskrit, STATUS_LABEL[e.status], e.room.ideal.join(", ")];
    }),
    columnStyles: { 0: { cellWidth: 8, halign: "center" }, 1: { cellWidth: 40, fontStyle: "bold" }, 2: { cellWidth: 18, halign: "center" }, 3: { cellWidth: 32 }, 4: { cellWidth: 28 }, 5: { cellWidth: "auto" } },
    didParseCell: (d) => {
      if (d.section !== "body" || d.column.index !== 4) return;
      const e = evalFor(markers[d.row.index]?.id);
      if (e) { d.cell.styles.textColor = hexToRgb(STATUS_COLORS[e.status]); d.cell.styles.fontStyle = "bold"; }
    },
  });
}

export function drawFooters(doc, branding) {
  const total = doc.getNumberOfPages();
  const left = hasBranding(branding)
    ? [branding.firm || branding.consultant_name, branding.phone, branding.email, branding.website].filter(Boolean).join("  ·  ")
    : "Vastu Mandala · Floor Plan Sector Analyzer";
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const { w, h } = pageSize(doc);
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(left, MARGIN, h - 6);
    doc.text(`Page ${i} of ${total}`, w - MARGIN, h - 6, { align: "right" });
  }
}
